import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import { ErrorText } from "../../StyledCss/styledComponents";
import {
  StyledInputGroup,
  StyledInputContainer,
  StyledTextarea,
  StyledLabel,
} from "../../StyledCss/styledInputs";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import PopupModal from "../PopupModal";
import { getPolicyRequests, updatePolicyRequest } from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const PolicyRequestsTab = ({ onView }) => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(FEATURE_CODES.POLICY_REQUEST_PDF_VIEW);
  const canApprove = hasPermission(FEATURE_CODES.POLICY_REQUEST_APPROVE);
  const canReject = hasPermission(FEATURE_CODES.POLICY_REQUEST_REJECT);
  const [requestsData, setRequestsData] = useState([]);
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");

  const fetchRequests = useCallback(async () => {
    setApiStatus(apiStatusConstants.inProgress);
    try {
      const response = await getPolicyRequests("Awaiting");
      if (response.ok) {
        const json = await response.json();
        if (json.status && json.data.length > 0) {
          const camelCaseData = json.data.map((item) =>
            convertKeysToCamelCase(item)
          );
          setRequestsData(camelCaseData);
        } else {
          setRequestsData([]);
        }
        setApiStatus(apiStatusConstants.success);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch {
      setApiStatus(apiStatusConstants.failure);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = useCallback(async (rowData) => {
    try {
      const response = await updatePolicyRequest(rowData.id, "Approved", "");
      if (response.ok) {
        toast.success(`Policy "${rowData.policyName}" has been approved.`);
        fetchRequests();
      } else {
        toast.error("Failed to approve policy request.");
      }
    } catch {
      toast.error("Something went wrong.");
    }
  }, [fetchRequests]);

  const openRejectModal = useCallback((rowData) => {
    setRejectingRequest(rowData);
    setRejectReason("");
    setRejectReasonError("");
    setRejectModalOpen(true);
  }, []);

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setRejectingRequest(null);
    setRejectReason("");
    setRejectReasonError("");
  };

  const onChangeRejectReason = (e) => {
    setRejectReason(e.target.value);
    setRejectReasonError("");
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) {
      setRejectReasonError("Please enter a reason.");
      return;
    }
    try {
      const response = await updatePolicyRequest(
        rejectingRequest.id,
        "Rejected",
        rejectReason
      );
      if (response.ok) {
        toast.success(
          `Policy "${rejectingRequest.policyName}" has been rejected.`
        );
        closeRejectModal();
        fetchRequests();
      } else {
        toast.error("Failed to reject policy request.");
      }
    } catch {
      toast.error("Something went wrong.");
    }
  };

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "dd-MMM-yyyy");
    } catch {
      return dateStr;
    }
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "POLICY NAME",
      field: "policyName",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 180,
    },
    {
      headerName: "UPLOADED BY",
      field: "hrName",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 130,
    },
    {
      headerName: "UPLOADED DATE",
      field: "createdAt",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 140,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "VISIBLE TO",
      field: "visibleTo",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 120,
    },
    ...(canView
      ? [
          {
            headerName: "VIEW POLICY",
            suppressMenu: true,
            minWidth: 110,
            cellRenderer: (params) => (
              <SecondaryButton
                className="d-flex justify-content-center align-items-center"
                onClick={() => onView(params.data)}
              >
                View
              </SecondaryButton>
            ),
          },
        ]
      : []),
    ...(canApprove
      ? [
          {
            headerName: "APPROVE",
            suppressMenu: true,
            minWidth: 110,
            cellRenderer: (params) => (
              <PrimaryButton
                className="d-flex justify-content-center align-items-center"
                onClick={() => handleApprove(params.data)}
              >
                Approve
              </PrimaryButton>
            ),
          },
        ]
      : []),
    ...(canReject
      ? [
          {
            headerName: "REJECT",
            suppressMenu: true,
            minWidth: 110,
            cellRenderer: (params) => (
              <SecondaryButton
                className="d-flex justify-content-center align-items-center"
                onClick={() => openRejectModal(params.data)}
              >
                Reject
              </SecondaryButton>
            ),
          },
        ]
      : []),
  ], [onView, handleApprove, openRejectModal, formatDate, canView, canApprove, canReject]);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 100,
    cellStyle: { fontSize: "14px" },
  }), []);

  const renderContent = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={fetchRequests} />;
      case apiStatusConstants.success:
        return (
          <div
            className="ag-theme-alpine"
            style={{ height: "65vh", margin: "15px", fontSize: "14px" }}
          >
            <AgGridReact
              rowData={requestsData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              suppressBrowserResizeObserver={true}
              pagination={true}
              paginationPageSize={20}
              enableCellTextSelection={true}
              alwaysShowVerticalScroll={true}
              overlayNoRowsTemplate="<span style='color:#757575;font-size:16px'>No pending policy requests</span>"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderContent()}

      <PopupModal
        modalName="Reject Policy Request"
        closeBtn={closeRejectModal}
        showModal={rejectModalOpen}
        paddingTop="18px"
        mb="mb-2"
      >
        <form onSubmit={submitReject}>
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledTextarea
                onChange={onChangeRejectReason}
                value={rejectReason}
              />
              <StyledLabel hasValue={true} className="user-label">
                Reason*
              </StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
          {rejectReasonError && (
            <ErrorText className="mt-3">*{rejectReasonError}</ErrorText>
          )}
          <div className="d-flex justify-content-end gap-3 mt-3">
            <SecondaryButton type="button" onClick={closeRejectModal}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={!!rejectReasonError}>
              Submit
            </PrimaryButton>
          </div>
        </form>
      </PopupModal>
    </>
  );
};

export default PolicyRequestsTab;
